<template>
  <div style="position: relative;">
    <!-- cropped player -->
    <div
        :style="{
        margin: '0 auto',
        position: 'relative',
        width: scaledCropWidth + 'px',
        height: scaledCropHeight + 'px',
        overflow: 'hidden'
        }"
    >
        <div
        :style="{
            position: 'absolute',
            left: -scaledCropX + 'px',
            top: -scaledCropY + 'px',
            width: scaledWidth + 'px',
            height: scaledHeight + 'px'
        }"
        >
        <edit-player
            ref="editPlayer"
            v-model="playerData"
            :mode="statusMode"
            :src="src"
            :thumbnail="thumbnail"
            :is-muted.sync="isMuted"
            :video-sizes="videoSizes"
            :crop-video-resize="cropVideoResize"
            :player-start-time="effectiveStartTime"
            :paused="playerPaused"
            :original-size="true"
            :hide-controls="true"
            @timeUpdate="handleTimeUpdate($event)"
            @updatePausedTrue="playerPaused = $event"
            @updatePausedFalse="playerPaused = $event"
        />
        </div>
        <!-- text overlay area -->
        <span
            v-if="textOverlay.enabled"
            :style='{
            "font-size": scaledTextSize + "px",
            "font-family": textOverlay.font,
            "color": textOverlay.color,
            "pointer-events": "none",
            "position": "absolute",
            "left": scaledTextX + "px",
            "top": scaledTextY + "px",
            "white-space": "nowrap"
            }'
        >{{ textOverlay.text }}</span>
        <!-- image overlay area -->
        <img
            v-if="imageOverlay.enabled"
            :src="imageOverlay.src"
            :style='{
            "pointer-events": "none",
            "position": "absolute",
            "left": scaledImageX + "px",
            "top": scaledImageY + "px",
            "width": scaledImageWidth + "px",
            "height": scaledImageHeight + "px"
            }'
        />
    </div>
    
    <!-- playback controls -->
    <div class="preview-player-controls">
      <div class="preview-player-buttons">
        <el-button-group>
        <el-button
          :type="playerPaused ? 'primary' : 'default'"
          @click="togglePlayPause"
          size="mini"
        >
          <play-circle-outline v-if="playerPaused" />
          <pause-circle-outline v-else />
        </el-button>
        <el-button
          type="default"
          @click="stopPlayback"
          size="mini"
        >
          <stop-circle-outline />
        </el-button>
      </el-button-group>
      </div>
      <el-slider
        v-model="playerTime"
        :min="effectiveStartTime"
        :max="effectiveEndTime"
        :step="0.1"
        :format-tooltip="formatTime"
        @change="handleSeek"
        class="preview-seek-bar"
      />
      <div class="preview-time-display">
        <span class="current-time">{{ formatTimeDisplay(playerTime - effectiveStartTime) }}</span>
        <span class="separator">/</span>
        <span class="total-time">{{ formatTimeDisplay(effectiveEndTime - effectiveStartTime) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import VideoPlayer from "@/components/media/video/VideoPlayer.vue";
import { MODES_DRAG, MODES_PREVIEW } from "./cut_modes";
import PlayCircleOutline from "vue-material-design-icons/PlayCircleOutline.vue";
import PauseCircleOutline from "vue-material-design-icons/PauseCircleOutline.vue";
import StopCircleOutline from "vue-material-design-icons/StopCircleOutline.vue";
import EditPlayer from "@/components/media/editing/EditPlayer.vue";
import { getDurationTimeline } from "@/utils/time.js";

// @group Editing
// Reactive player for edit panel
export default {
  name: "CropPreviewPlayer",
  components: {
    EditPlayer,
    PlayCircleOutline,
    PauseCircleOutline,
    StopCircleOutline
  } /*
  model: {
    prop: "playerData",
    event: "playerDataChanged"
  },*/,
  props: {
    // Crop X position as ratio to video width (0-1)
    cropX: {
      type: Number,
      default: 0,
      validator(value) {
        return value >= 0 && value <= 1;
      }
    },
    // Crop Y position as ratio to video height (0-1)
    cropY: {
      type: Number,
      default: 0,
      validator(value) {
        return value >= 0 && value <= 1;
      }
    },
    // Crop width as ratio to video width (0-1)
    cropWidth: {
      type: Number,
      default: 1,
      validator(value) {
        return value >= 0 && value <= 1;
      }
    },
    // Crop height as ratio to video height (0-1)
    cropHeight: {
      type: Number,
      default: 1,
      validator(value) {
        return value >= 0 && value <= 1;
      }
    },
    statusMode: {
      type: String,
      default: "drag"
    },
    startPreviewEmitValue: {
      type: Number,
      default: 0
    },
    playerStartTime: {
      type: Number,
      default: 0
    },
    // Player data object (optional - will create internal if not provided)
    value: {
      type: Object,
      required: false,
      default: null
    },
    // Thumbnail link
    thumbnail: {
      type: String,
      default: null
    },
    // Source video link
    src: {
      type: [Object, String],
      required: true
    },
    // Synchronized muted flag
    isMuted: {
      type: Boolean,
      default: false
    },
    cropVideoResize: {
      type: Boolean,
      default: false
    },
    // Video width and heigth in pixels
    videoSizes: {
      type: Object,
      required: true,
      vaidator(val) {
        const { width, height } = val;
        return Number.isInteger(width) && Number.isInteger(height);
      }
    },
    textOverlay: {
      type: Object,
      default: () => ({
        enabled: false,
        text: "",
        font: "",
        color: "",
        size: 0,
        x: 0,
        y: 0
      })
    },
    imageOverlay: {
      type: Object,
      default: () => ({
        enabled: false,
        src: null,
        x: 0,
        y: 0,
        width: 0,
        height: 0
      })
    },
    // Cut range start time (in seconds)
    cutStart: {
      type: Number,
      default: undefined
    },
    // Cut range end time (in seconds)
    cutEnd: {
      type: Number,
      default: undefined
    }
  },
  /** VideoJS player options */
  playerOptions: {
    /** Autoplay need to initialize player area at start,
     * otherwise crop/text/image areas will can go beyound
     * player boundary */
    autoplay: true,
    controls: true,
    fill: true,
    preload: "none",
    cors: true,
    children: {
      controlBar: {
        captionsButton: false,
        chaptersButton: false,
        fullscreenToggle: false,
        subtitlesButton: false,
        playbackRateMenuButton: false,
        pictureInPictureToggle: false
      }
    }
  },
  data() {
    return {
      playerOptions: {
        /** Autoplay need to initialize player area at start,
         * otherwise crop/text/image areas will can go beyound
         * player boundary */
        autoplay: true,
        controls: true,
        fill: true,
        preload: "none",
        cors: true,
        children: {
          controlBar: {
            captionsButton: false,
            chaptersButton: false,
            fullscreenToggle: false,
            subtitlesButton: false,
            playbackRateMenuButton: false,
            pictureInPictureToggle: false
          }
        }
      },
      stopPreviewWatch: null,
      time: 0,
      // paused: this.playerData.paused,
      loading: true,
      duration: 10,
      // Internal player data if value prop is not provided
      playerData: {
        time: 0,
        paused: true,
        loading: true,
        duration: 10
      },
      playerTime: 0,
      // Flag to track if preview needs to be restarted (stopped manually or reached end)
      needsRestart: false,
      // Player size settings
      relativeMaxWidth: 0.8,
      relativeMaxHeight: 0.5,
      width: 800,
      height: 640,
      playerPaused: true,

      maxWidth: 640,
      maxHeight: 360,
      playerSizeWatcher: null,
      // Internal state for preview range tracking
      internalStartTime: 0,
      // Flag to prevent time update conflicts during seeking
      isSeeking: false,
    };
  },
  mounted() {
    const container = this.$el.parentElement;
    if(container){
        const containerComputedStyle = getComputedStyle(container);
        this.maxWidth = container.clientWidth - parseFloat(containerComputedStyle.paddingLeft) - parseFloat(containerComputedStyle.paddingRight);
        this.maxHeight = window.innerHeight * 0.6; // container.clientHeight - parseFloat(containerComputedStyle.paddingTop) - parseFloat(containerComputedStyle.paddingBottom);
        this.playerSizeWatcher = new ResizeObserver(() => {
            const containerComputedStyle = getComputedStyle(container);
            this.maxWidth = container.clientWidth - parseFloat(containerComputedStyle.paddingLeft) - parseFloat(containerComputedStyle.paddingRight);
            this.maxHeight = window.innerHeight * 0.6; // container.clientHeight - parseFloat(containerComputedStyle.paddingTop) - parseFloat(containerComputedStyle.paddingBottom);
        });
        this.playerSizeWatcher.observe(container);
    }
  },
  beforeDestroy() {
    // Clean up watchers
    if (typeof this.stopPreviewWatch === 'function') {
      this.stopPreviewWatch();
      this.stopPreviewWatch = null;
    }
    if(this.playerSizeWatcher) {
      this.playerSizeWatcher.disconnect();
      this.playerSizeWatcher = null;
    }
  },
  computed: {
    // Available width for the player (not to be confused with props.playerWidth)
    availablePlayerWidth() {
        return Math.min(this.videoCropWidth, this.maxWidth);
    },
    // Available height for the player (not to be confused with props.playerHeight)
    availablePlayerHeight() {
        return Math.min(this.videoCropHeight, this.maxHeight);
    },
    // Convert crop ratios to video pixel dimensions
    videoCropWidth() {
      return this.cropWidth * this.videoSizes.width;
    },
    videoCropHeight() {
      return this.cropHeight * this.videoSizes.height;
    },
    videoCropX() {
      return this.cropX * this.videoSizes.width;
    },
    videoCropY() {
      return this.cropY * this.videoSizes.height;
    },
    // Single scale factor to fit crop area into player size
    // This replaces the previous two-step scaling (cropPreviewScale * viewportScale)
    scale() {
      if (!this.videoCropWidth || !this.videoCropHeight) {
        return 1;
      }
      // Calculate scale to fit crop area into available player space
      const scaleX = this.availablePlayerWidth / this.videoCropWidth;
      const scaleY = this.availablePlayerHeight / this.videoCropHeight;
      // Use minimum to ensure crop area fits, but don't scale up (max 1)
      return Math.min(1, scaleX, scaleY);
    },
    // Scaled dimensions for display
    scaledCropWidth() {
      return this.videoCropWidth * this.scale;
    },
    scaledCropHeight() {
      return this.videoCropHeight * this.scale;
    },
    scaledCropX() {
      return this.videoCropX * this.scale;
    },
    scaledCropY() {
      return this.videoCropY * this.scale;
    },
    // Full video dimensions scaled to fit
    scaledWidth() {
      return this.videoSizes.width * this.scale;
    },
    scaledHeight() {
      return this.videoSizes.height * this.scale;
    },
    // @vuese
    // Text size scaled to preview player size
    // textOverlay.size is in pixels of original video, needs to be scaled
    scaledTextSize() {
      if (!this.textOverlay.enabled || !this.textOverlay.size) {
        return 0;
      }
      // Scale text size from original video pixels to preview player pixels
      return this.textOverlay.size * this.scale;
    },
    // @vuese
    // Text X position in preview player pixels
    // textOverlay.x is ratio (0-1) relative to video width
    scaledTextX() {
      if (!this.textOverlay.enabled) {
        return 0;
      }
      // Convert ratio to video pixels
      const textXVideo = this.textOverlay.x * this.videoSizes.width;
      // Convert to relative position within crop area
      const textXRelative = textXVideo - this.videoCropX;
      // Scale to preview player pixels
      return textXRelative * this.scale;
    },
    // @vuese
    // Text Y position in preview player pixels
    // textOverlay.y is ratio (0-1) relative to video height
    scaledTextY() {
      if (!this.textOverlay.enabled) {
        return 0;
      }
      // Convert ratio to video pixels
      const textYVideo = this.textOverlay.y * this.videoSizes.height;
      // Convert to relative position within crop area
      const textYRelative = textYVideo - this.videoCropY;
      // Scale to preview player pixels
      return textYRelative * this.scale;
    },
    // @vuese
    // Image X position in preview player pixels
    // imageOverlay.x is ratio (0-1) relative to video width
    scaledImageX() {
      if (!this.imageOverlay.enabled) {
        return 0;
      }
      // Convert ratio to video pixels
      const imageXVideo = this.imageOverlay.x * this.videoSizes.width;
      // Convert to relative position within crop area
      const imageXRelative = imageXVideo - this.videoCropX;
      // Scale to preview player pixels
      return imageXRelative * this.scale;
    },
    // @vuese
    // Image Y position in preview player pixels
    // imageOverlay.y is ratio (0-1) relative to video height
    scaledImageY() {
      if (!this.imageOverlay.enabled) {
        return 0;
      }
      // Convert ratio to video pixels
      const imageYVideo = this.imageOverlay.y * this.videoSizes.height;
      // Convert to relative position within crop area
      const imageYRelative = imageYVideo - this.videoCropY;
      // Scale to preview player pixels
      return imageYRelative * this.scale;
    },
    // @vuese
    // Image width in preview player pixels
    // imageOverlay.width is ratio (0-1) relative to video width
    scaledImageWidth() {
      if (!this.imageOverlay.enabled) {
        return 0;
      }
      // Convert ratio to video pixels
      const imageWidthVideo = this.imageOverlay.width * this.videoSizes.width;
      // Scale to preview player pixels
      return imageWidthVideo * this.scale;
    },
    // @vuese
    // Image height in preview player pixels
    // imageOverlay.height is ratio (0-1) relative to video height
    scaledImageHeight() {
      if (!this.imageOverlay.enabled) {
        return 0;
      }
      // Convert ratio to video pixels
      const imageHeightVideo = this.imageOverlay.height * this.videoSizes.height;
      // Scale to preview player pixels
      return imageHeightVideo * this.scale;
    },
    // Use internal state for preview with cut range, props otherwise
    effectivePaused() {
      if (this.cutStart !== undefined && this.cutEnd !== undefined) {
        return this.internalPaused;
      }
      return this.paused;
    },
    effectiveStartTime() {
      if (this.cutStart !== undefined && this.cutEnd !== undefined) {
        return this.internalStartTime;
      }
      return this.playerStartTime;
    },
    // @vuese
    // Effective end time for seek bar (cutEnd or full duration)
    effectiveEndTime() {
      if (this.cutEnd !== undefined) {
        return this.cutEnd;
      }
      return this.playerData.duration || 10;
    }
  },
  methods: {
    setTime(e) {
      this.$refs.editPlayer.setTime(e);
    },
    // @vuese
    // Handle time update from player (prevent conflicts during seeking)
    handleTimeUpdate(time) {
      if (!this.isSeeking) {
        this.playerTime = time;
      }
    },
    // @vuese
    // Handle seek bar change
    handleSeek(newTime) {
      this.isSeeking = true;
      this.setTime(newTime);
      // Reset seeking flag after a short delay
      this.$nextTick(() => {
        setTimeout(() => {
          this.isSeeking = false;
        }, 100);
      });
    },
    // @vuese
    // Format time for tooltip
    formatTime(value) {
      return getDurationTimeline(value);
    },
    // @vuese
    // Format time for display (MM:SS or HH:MM:SS)
    formatTimeDisplay(time) {
      if (!time && time !== 0) return "00:00";
      return getDurationTimeline(time);
    },
    // @vuese
    // Toggle play/pause state
    togglePlayPause() {
      if (this.playerPaused) {
        // Video is paused - check if we need to restart preview
        if (this.needsRestart) {
          // Video stopped or reached end of range - restart preview
          this.startPreview(this.cutStart, this.cutEnd);
        } else {
          // Video paused in middle of range - just resume
          this.playerPaused = false;
          this.$emit("updatePausedFalse", false);
        }
      } else {
        // Video is playing - pause it
        this.playerPaused = true;
        this.$emit("updatePausedTrue", true);
      }
    },
    // @vuese
    // Stop playback and reset to start time
    stopPlayback() {
      this.$emit("updatePausedTrue", true);
      if (this.$refs.editPlayer) {
        const resetTime = this.effectiveStartTime;
        this.$refs.editPlayer.setTime(resetTime);
        this.playerTime = resetTime; // Sync seek bar position
        if (this.cutStart !== undefined && this.cutEnd !== undefined) {
          this.playerPaused = true;
          this.internalStartTime = resetTime;
          this.needsRestart = true; // Mark that preview needs to be restarted
        }
      }
    },
    // @vuese
    // Wait for video to load
    async waitForLoad() {
      return new Promise((res, rej) => {
        if (this.playerData.loading === false) {
          return res();
        }
        let unwatch = null;
        unwatch = this.$watch('playerData', (playerData) => {
          if (playerData.loading === false) {
            if (unwatch) {
              unwatch();
            }
            res();
          }
        }, { deep: true });
        setTimeout(() => {
          if (unwatch) {
            unwatch();
          }
          rej(new Error('Video load timeout'));
        }, 10000);
      });
    },
    // @vuese
    // Start preview with given range
    async startPreview(start, end) {
      // Stop any existing watcher
      if (typeof this.stopPreviewWatch === 'function') {
        this.stopPreviewWatch();
        this.stopPreviewWatch = null;
      }

      // Set start time
      this.internalStartTime = start;

      // Wait for video to load
      try {
        await this.waitForLoad();
      } catch (e) {
        console.error('Failed to load video for preview:', e);
        return;
      }

      // Set time and start playback
      this.$refs.editPlayer.setTime(start);
      this.playerTime = start; // Sync seek bar position
      this.playerPaused = false;
      this.needsRestart = false; // Reset flag when preview starts

      // Start watching for range boundaries
      this.startRangeWatch(start, end);
    },
    // @vuese
    // Start watching for range boundaries
    startRangeWatch(start, end) {
      // Stop any existing watcher
      if (typeof this.stopPreviewWatch === 'function') {
        this.stopPreviewWatch();
      }

      console.log('watch range')

      let firstTimeUpdate = true;
      this.stopPreviewWatch = this.$watch('playerData.time', (currentTime) => {
        if (firstTimeUpdate) {
          firstTimeUpdate = false;
          return;
        }
        if (currentTime < start || currentTime > end) {
          console.warn("STOP! Time exceeded range:", currentTime, start, end);
          this.handleRangeExceeded(start);
        }
      });
    },
    // @vuese
    // Handle when playback exceeds range boundaries
    handleRangeExceeded(resetTime) {
      const time = resetTime !== undefined ? resetTime : (this.cutStart !== undefined ? this.cutStart : 0);
      this.playerPaused = true;
      this.internalStartTime = time;
      this.needsRestart = true; // Mark that preview needs to be restarted
      if (this.$refs.editPlayer) {
        this.$refs.editPlayer.setTime(time);
      }
      this.playerTime = time; // Sync seek bar position
      // Stop the watcher
      if (typeof this.stopPreviewWatch === 'function') {
        this.stopPreviewWatch();
        this.stopPreviewWatch = null;
      }
    },
    // @vuese
    // Stop preview playback completely (called when modal is closed)
    stopPreview() {
      // Stop the watcher
      if (typeof this.stopPreviewWatch === 'function') {
        this.stopPreviewWatch();
        this.stopPreviewWatch = null;
      }
      
      // Stop playback
      this.playerPaused = true;
      this.$emit("updatePausedTrue", true);
      
      // Reset to start of range if cut range is defined
      if (this.cutStart !== undefined && this.cutEnd !== undefined) {
        this.internalStartTime = this.cutStart;
        this.needsRestart = true;
        if (this.$refs.editPlayer) {
          this.$refs.editPlayer.setTime(this.cutStart);
        }
        this.playerTime = this.cutStart; // Sync seek bar position
      }
    }
  }
};
</script>

<style scoped>
.edit-form-player {
  height: 100%;
  max-width: 100%;
}

.preview-player-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  padding: 10px 5px;
}
.preview-player-buttons {
  white-space: nowrap;
  margin-right: 5px;
  flex-shrink: 0;
}

.preview-seek-bar-container {
  margin-top: 15px;
  padding: 0 20px;
}

.preview-seek-bar {
  width: 100%;
}

.preview-time-display {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  font-size: 12px;
}

.preview-time-display .current-time {
  font-weight: 500;
}

.preview-time-display .separator {
  color: #909399;
}

.preview-time-display .total-time {
  color: #909399;
}
</style>

<style>
.edit-form-player .vjs-tech {
  /* width: auto !important; */
  width: 100%;
  height: 100%;
}
</style>
